import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import { useDebouncedValue } from './search';
import type { EmailDraftData, EmailPreviewData, MailDispatchData, MailRecipientsData } from '@/types/api';

/** The emails that open in the composer from a record. Keys match `mail_dispatches.mailable`. */
export type EmailType =
  | 'invoice.sent'
  | 'invoice.reminder'
  | 'invoice.receipt'
  | 'approvable.invitation'
  | 'approvable.reminder'
  | 'subscription.receipt';

export type InvitationFields = {
  name: string;
  email: string;
  role: 'admin' | 'client_user';
  client_id?: number | null;
};

/**
 * What an email is about. A record already exists; an invitation is composed
 * from the invite form before Send creates it.
 */
export type EmailSource =
  | { kind: 'record'; type: EmailType; id: number }
  | { kind: 'invitation'; fields: InvitationFields };

/** The admin's wording for one send, sent alongside the recipients. */
export type EmailWording = { subject: string; message: string };

/**
 * The composer's starting point: the standard subject and message, who it can
 * go to, and what's attached. Fetched fresh on every opening so a contact or
 * amount edited elsewhere shows up.
 */
export function useEmailDraft(source: EmailSource, enabled: boolean) {
  return useQuery({
    queryKey: ['email-draft', source],
    enabled,
    staleTime: 0,
    gcTime: 0,
    queryFn: async (): Promise<EmailDraftData> => {
      if (source.kind === 'record') {
        const { data } = await api.get<EmailDraftData>(`/v1/emails/${source.type}/${source.id}`);
        return data;
      }
      await primeCsrf();
      const { data } = await api.post<EmailDraftData>('/v1/invitations/email-draft', source.fields);
      return data;
    },
  });
}

/**
 * The email as it will arrive, rendered by the server from the real template.
 * Waits for typing to pause, and keeps showing the last render while the next
 * one loads so the preview doesn't blink on every keystroke.
 */
export function useEmailPreview(source: EmailSource, wording: EmailWording, enabled: boolean) {
  const subject = useDebouncedValue(wording.subject, 400);
  const message = useDebouncedValue(wording.message, 400);

  return useQuery({
    queryKey: ['email-preview', source, subject, message],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<EmailPreviewData> => {
      await primeCsrf();
      const { data } =
        source.kind === 'record'
          ? await api.post<EmailPreviewData>(`/v1/emails/${source.type}/${source.id}/preview`, {
              subject,
              message,
            })
          : await api.post<EmailPreviewData>('/v1/invitations/email-preview', {
              ...source.fields,
              subject,
              message,
            });
      return data;
    },
  });
}

/** One sent email, as kept at send time. */
export function useMailDispatch(id: number | null) {
  return useQuery({
    queryKey: ['mail-dispatches', id],
    enabled: id !== null,
    queryFn: async (): Promise<MailDispatchData> => {
      const { data } = await api.get<MailDispatchData>(`/v1/mail-dispatches/${id}`);
      return data;
    },
  });
}

/**
 * "Hi Dana," for Dana Velasquez. Must match ComposableMailable::greeting() on
 * the server, which writes the same line for sends nobody reviews.
 */
export function greetingFor(name: string | null | undefined): string {
  const first = (name ?? '').trim().split(/\s+/)[0] ?? '';
  return first ? `Hi ${first},` : 'Hi there,';
}

/**
 * The contact behind the first To address, for the greeting. Contacts only:
 * the client record's "name" is the company, which never greets anyone.
 */
export function greetingNameFor(recipients: MailRecipientsData | null | undefined, to: string[]): string | null {
  const primary = to[0]?.toLowerCase();
  if (!primary) return null;
  const option = recipients?.options.find(
    (o) => o.source === 'contact' && o.address.toLowerCase() === primary,
  );
  return option?.name ?? null;
}

/**
 * Open an attachment the client will receive. A path is an API route that
 * needs the session cookie, so it is fetched through axios (the SPA and the
 * API live on different hosts); anything else is a ready link.
 */
export async function openEmailAttachment(url: string): Promise<void> {
  if (!url.startsWith('/')) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  const { data } = await api.get<Blob>(url, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(data);
  window.open(objectUrl, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
